import classNames from 'classnames';
import { navigate } from 'gatsby';
import React, { use } from 'react';

import type { PageLanguage } from 'src/components/common/page-context';
import { SiteContext } from 'src/context/site-context';
import type { BuildType } from 'src/enums/build/build-type';
import { shouldIncludeLanguageInUrlForBuildType } from 'src/enums/build/build-type';
import type { Language } from 'src/enums/language';
import { getUrlValueForLanguage } from 'src/enums/language';
import { preparePath } from 'src/helpers/navigation-helper';
import { trimPath } from 'src/helpers/path-matching-helper';
import { updateUrlPreservingQueryParams } from 'src/helpers/query-string-helper';

import LanguageItem from './language-item';
import * as styles from './language-picker-popup.module.scss';

const navigateToPathForLanguage = (language: Language, path: string, buildType: BuildType) => {
  const trimmedPath = trimPath(path);
  const pathWithLanguage = shouldIncludeLanguageInUrlForBuildType(buildType)
    ? `/${getUrlValueForLanguage(language)}/${trimmedPath}/`
    : `/${trimmedPath}/`;

  navigate(updateUrlPreservingQueryParams(preparePath(pathWithLanguage)));
};

type LanguagePickerPopupProps = {
  currentLanguage: Language;
  pageLanguages: PageLanguage[];
};

const LanguagePickerPopup: React.FC<LanguagePickerPopupProps> = ({
  currentLanguage,
  pageLanguages,
}) => {
  const { buildType } = use(SiteContext);
  return (
    <div className={styles.popup}>
      <div className={classNames(styles.item, styles.currentItem)}>
        <LanguageItem language={currentLanguage} />
      </div>
      {pageLanguages
        .filter(({ language }) => language !== currentLanguage)
        .map(({ language, path }) => (
          <div
            key={language}
            className={classNames(styles.item, styles.alternativeItem, styles.clickableItem)}
            onClick={() => navigateToPathForLanguage(language, path, buildType)}
          >
            <LanguageItem language={language} />
          </div>
        ))}
    </div>
  );
};

export default LanguagePickerPopup;
